import React, { useState, useCallback, useMemo } from 'react';
import { Container, Table, Button, Row, Col, Spinner } from 'react-bootstrap';
import { UploadModal, CommentModal } from './components/modal/modal';
import ReportsService from '../../../../../../services/reports-service';
import { ExcelPreviewModal } from '../modal/excel-preview-modal/excel-preview-modal';
import * as XLSX from 'xlsx';

const MAX_PREVIEW_ROWS = 500; // limit rows for performance

const EventBasedReportContainer = ({
  items,
  handleCheckboxChange,
  handleSendReport,
  handleReject
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [uploadModalData, setUploadModalData] = useState({
    show: false,
    report: null,
    item: null,
  });

  // Excel preview states
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [excelPreviewMeta, setExcelPreviewMeta] = useState(null);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);

  // Track loading state per item ID (Set stored in state)
  const [loadingPreviewIds, setLoadingPreviewIds] = useState(new Set());

  const [commentModalData, setCommentModalData] = useState({ show: false, comment: '' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const reportService = new ReportsService();

  // Close upload modal
  const handleUploadModalClose = useCallback(() => {
    setUploadModalData({ show: false });
  }, []);

  // Utility: open with Office Online viewer
  const openOfficeViewer = (url) => {
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    const newWindow = window.open(viewerUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  // Utility: detect Excel by extension or content-type
  const checkExcelByExtOrType = (urlPathLower, contentType = '') => {
    return (
      urlPathLower.endsWith('.xlsx') ||
      urlPathLower.endsWith('.xls') ||
      contentType.includes('spreadsheet') ||
      contentType.includes('officedocument')
    );
  };

  // Utility: build S3 prefix logic (same as before)
  const buildS3Prefix = (report) => {
    const sanitize = (str) => (str ? String(str).replace(/\//g, '-') : '');
    const title = sanitize(report.reportTitle);
    const isin = sanitize(report.isin);
    if (
      report.reportTitle.startsWith('Closure of Registry Service') ||
      report.reportTitle.startsWith('Consent Solicitation Entitlement') ||
      report.reportTitle.startsWith('Credit Letter')
    ) {
      const paymentDate = sanitize(report.paymentDate);
      return `Ad-hoc Reports/${isin}/${title}/${paymentDate}/`;
    } else {
      const bp = sanitize(report.bpId);
      const holder = sanitize(report.holder);
      const df = sanitize(report.dateFrom);
      const dt = sanitize(report.dateTo);
      const dateRange = df || dt ? `${df}${dt}` : '';
      return `Ad-hoc Reports/${isin}/${title}/${bp}/${holder}/${dateRange}/`;
    }
  };

  // Preview handler
  const handlePreviewClick = async (report) => {
    const id = report.id;
    setLoadingPreviewIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  
    try {
      const s3Prefix = buildS3Prefix(report);
      const response = await reportService.retrieveEventBasedReport(s3Prefix);
      const fileURL = response.fileURL;
      if (!fileURL) throw new Error('No file URL returned');
  
      // Attempt HEAD to get content-type and size
      let contentType = '';
      let contentLength = 0;
      try {
        const headRes = await fetch(fileURL, { method: 'HEAD' });
        if (headRes.ok) {
          contentType = headRes.headers.get('Content-Type') || '';
          const len = headRes.headers.get('Content-Length');
          contentLength = len ? parseInt(len, 10) : 0;
        }
      } catch (e) {
        console.warn('HEAD request failed:', e);
      }
  
      const urlObj = new URL(fileURL);
      const pathLower = urlObj.pathname.toLowerCase();
  
      const isPdf = pathLower.endsWith('.pdf') || contentType.includes('pdf');
      if (isPdf) {
        const newWindow = window.open(fileURL, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
        return;
      }
  
      const isExcel = checkExcelByExtOrType(pathLower, contentType);
      if (!isExcel) {
        // Other non-Excel/non-PDF: use Office Online viewer fallback
        openOfficeViewer(fileURL);
        return;
      }
  
      const MAX_BYTES = 5 * 1024 * 1024;
      if (contentLength > MAX_BYTES) {
        const proceed = window.confirm(
          `The file is large (~${(contentLength/1e6).toFixed(1)} MB). Preview may be slow. Continue?`
        );
        if (!proceed) return;
      }
  
      const fileRes = await fetch(fileURL);
      if (!fileRes.ok) throw new Error(`Fetch failed (${fileRes.status})`);
      const blob = await fileRes.blob();
      const arrayBuffer = await blob.arrayBuffer();
  
      let workbook;
      try {
        workbook = XLSX.read(arrayBuffer, { type: 'array' });
      } catch (parseErr) {
        console.error('Excel parse error:', parseErr);
        openOfficeViewer(fileURL);
        return;
      }
  
      const sheetNames = workbook.SheetNames;
      const sheetName = sheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rawData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      const totalRows = rawData.length;
      const usedRows = totalRows > MAX_PREVIEW_ROWS ? rawData.slice(0, MAX_PREVIEW_ROWS) : rawData;
      const maxCols = usedRows.reduce((m, row) => Math.max(m, row.length), 0);
  
      const normalizedData = usedRows.map(row =>
        Array.from({ length: maxCols }, (_, i) => ({
          value: row[i] !== undefined ? row[i] : '',
          readOnly: true
        }))
      );
  
      setExcelPreviewData(normalizedData);
      setExcelPreviewMeta({
        sheetName,
        sheetNames,
        totalRows,
        totalCols: maxCols
      });
      setShowExcelPreviewModal(true);
  
    } catch (error) {
      console.error('Error fetching report preview:', error);
      alert('Failed to load preview: ' + (error.message || error));
    } finally {
      setLoadingPreviewIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };  

  // Download logic unchanged
  const handleDownloadClick = async (report) => {
    try {
      const s3Prefix = buildS3Prefix(report);
      const response = await reportService.retrieveEventBasedReport(s3Prefix);
      if (response && response.fileURL) {
        const url = response.fileURL;
        const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);
        const fileRes = await fetch(url);
        if (!fileRes.ok) throw new Error('Network response was not ok');
        const blob = await fileRes.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        console.error('Download: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report download:', error);
      alert('Failed to download file: ' + (error.message || error));
    }
  };

  // Upload click
  const handleUploadClick = useCallback((item) => {
    setUploadModalData({ show: true, report: null, item });
  }, []);

  const handleLocalCheckboxChange = (id) => (e) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, id]);
    } else {
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
    }
    if (handleCheckboxChange) {
      handleCheckboxChange(id);
    }
  };

  // Sorting by dateSubmitted
  const handleSortByDate = () => {
    setSortConfig((prev) => {
      if (prev.key === 'dateSubmitted') {
        return { key: 'dateSubmitted', direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: 'dateSubmitted', direction: 'asc' };
    });
  };

  const sortedItems = useMemo(() => {
    if (!items || !Array.isArray(items)) return [];
    const arr = [...items];
    if (sortConfig.key === 'dateSubmitted') {
      arr.sort((a, b) => {
        const da = new Date(a.dateSubmitted);
        const db = new Date(b.dateSubmitted);
        if (isNaN(da) || isNaN(db)) {
          const va = a.dateSubmitted || '';
          const vb = b.dateSubmitted || '';
          if (va < vb) return sortConfig.direction === 'asc' ? -1 : 1;
          if (va > vb) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        if (da < db) return sortConfig.direction === 'asc' ? -1 : 1;
        if (da > db) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [items, sortConfig]);

  return (
    <Container
      fluid
      className="event-based-report-container"
      style={{ display: 'flex', flexDirection: 'column', height: '70vh' }}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Report Name</th>
              <th>Issuer</th>
              <th>ISIN</th>
              <th style={{ cursor: 'pointer' }} onClick={handleSortByDate}>
                Date Submitted{' '}
                {sortConfig.key === 'dateSubmitted'
                  ? sortConfig.direction === 'asc' ? '▲' : '▼'
                  : ''}
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems && sortedItems.length > 0 ? (
              sortedItems.map((item, index) => {
                const isLoading = loadingPreviewIds.has(item.id);
                return (
                  <tr key={index}>
                    <td>
                      <label style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          style={{ marginRight: '8px' }}
                          disabled={item.status !== 'For Checking'}
                          checked={selectedItems.includes(item.id)}
                          onChange={handleLocalCheckboxChange(item.id)}
                        />
                      </label>
                    </td>
                    <td>
                      {item.reportTitle === 'BIR 2306 Request Letter' ? (
                        <>
                          {'BIR 2306 Request Letter'}<br />
                          {`BPID: ${item.bpId || ''}`}<br />
                          {`Holder Name: ${item.holder || ''}`}
                        </>
                      ) : item.reportTitle === 'Closure of Registry Service / Payment Account' ? (
                        <>
                          {'Closure of Registry Service / Payment Account'}<br />
                          {`Maturity Date: ${item.maturityDate || ''}`}
                        </>
                      ) : item.reportTitle === 'Credit Letter' ? (
                        <>
                          {'Credit Letter'}<br />
                          {`Payment Date: ${item.paymentDate || ''}`}
                        </>
                      ) : (
                        item.reportTitle
                      )}
                    </td>
                    <td>{item.securityIssuer}</td>
                    <td>{item.isin}</td>
                    <td>{item.dateSubmitted}</td>
                    <td>
                      {item.status}
                      {item.comment && (
                        <span
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          onClick={() => setCommentModalData({ show: true, comment: item.comment })}
                        >
                          &#9865;
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {(item.status === 'Report Available' || item.status === 'For Checking') && (
                          <Button
                            variant="primary"
                            className="action-button"
                            onClick={() => handleUploadClick(item)}
                          >
                            Upload
                          </Button>
                        )}
                        {(item.status !== 'For Generation' || item.status === 'For Checking') && (
                          <>
                            <Button
                              variant="primary"
                              className="action-button"
                              onClick={() => handlePreviewClick(item)}
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <>
                                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Previewing...
                                </>
                              ) : (
                                'Preview'
                              )}
                            </Button>
                            <Button
                              variant="primary"
                              className="action-button"
                              onClick={() => handleDownloadClick(item)}
                            >
                              Download
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  No Reports
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button
            variant="danger"
            className="mr-2"
            onClick={handleReject || (() => {})}
          >
            Reject Report
          </Button>
          <Button
            variant="primary"
            onClick={handleSendReport || (() => {})}
          >
            Publish Report to Issuer
          </Button>
        </Col>
      </Row>

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />

      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={() => setCommentModalData({ show: false, comment: '' })}
      />

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          meta={excelPreviewMeta} // you can adjust ExcelPreviewModal to display sheetName/totalRows info
          onClose={() => {
            setShowExcelPreviewModal(false);
            setExcelPreviewData([]);
            setExcelPreviewMeta(null);
          }}
        />
      )}
    </Container>
  );
};

export default EventBasedReportContainer;
